// WU-2.1 — broadcast announcements: the shared record shape + the two
// cross-layer predicates.
//
// Both rules are evaluated on the api (fetch-path filtering, route gating)
// AND on the web client (the WS live-push path re-derives visibility because
// user/supervisor-targeted frames ride the tenant-wide `queue:<tenant>`
// channel). Single-homing them here keeps the two sides from drifting — a
// widened supervisor set or changed expiry semantics lands on both at once.
/** Permissions whose holders count as the "supervisors" announcement audience. */
export const ANNOUNCEMENT_SUPERVISOR_PERMISSIONS = [
    "supervisor.queue_monitor",
    "admin",
];
/** Does this permission set qualify for the `supervisors` audience? */
export function canSeeSupervisorAudience(permissions) {
    return ANNOUNCEMENT_SUPERVISOR_PERMISSIONS.some((p) => permissions.includes(p));
}
/**
 * Is the announcement currently active? Canonical rule:
 * `clearedAt IS NULL AND (expiresAt IS NULL OR expiresAt > now)`.
 * The api service's Prisma `activeWhere` fragment mirrors this — change both
 * together.
 */
export function isAnnouncementActive(a, now) {
    if (a.clearedAt !== null)
        return false;
    if (a.expiresAt === null)
        return true;
    const expires = typeof a.expiresAt === "string" ? Date.parse(a.expiresAt) : a.expiresAt.getTime();
    return expires > now;
}
/**
 * Audience gate: does an announcement reach this viewer?
 * tenant → everyone; user → only the targeted user; supervisors → holders of
 * ANNOUNCEMENT_SUPERVISOR_PERMISSIONS; team → members of the resolved team subtree.
 * Null viewer sees nothing.
 */
export function isAnnouncementForViewer(a, viewer) {
    if (!viewer)
        return false;
    switch (a.audienceType) {
        case "tenant":
            return true;
        case "user":
            return a.audienceUserId === viewer.userId;
        case "supervisors":
            return canSeeSupervisorAudience(viewer.permissions);
        case "team":
            // Server-resolved decision shape (spec §6): viewer.teamIds is the set of
            // teams whose subtree contains the viewer (resolveAnnouncementTeamIds).
            // Fails closed with no context or a malformed record.
            return a.teamId != null && (viewer.teamIds ?? []).includes(a.teamId);
        default:
            return false;
    }
}
//# sourceMappingURL=announcements.js.map